import {isUndefined} from 'lodash';

import {
  AnalyticsHeatMapMetric,
  AnalyticsScalarMetricsEnum,
  AnalyticsTableMetric,
} from '../../../../../models/api/analyticsMetricsModel';
import {SeparatorsEnum} from '../../../../helpers/analyticsExportHelpers';
import {renderWeekDayFull} from '../../../../helpers/analyticsFormatHelpers';
import {getMetricLabels} from '../../../../helpers/metricLabels';
import {isScalarMetric} from '../../../../helpers/reportHelpers/analyticsReportHelpers';
import {DataAccessor} from '../../../charts/commons/accessors';
import {AnalyticsDataPointValueType, computeCsvQuote} from '../../../exports/helpers/metricExports';
import {ChartDataWithSeries} from '../../../reportViewer/workloadReport/workloadOverTime/workloadOverTimeMetrics';
import {AnyGraphMetricsEnum} from '../../helperTypes';

/**
 * Helper function that returns a closure that when called with a separator will generate a
 * CSV formatted string of the chart series data (used in the line chart and bar chart).
 * Used for exporting the metric level data from an individual chart
 */
export function generateCsvFormattedDataForChart<T>(
  xAccessor: DataAccessor<T>,
  data: ChartDataWithSeries<T>,
  selectedMetricId?: AnyGraphMetricsEnum,
) {
  return (separator: SeparatorsEnum) => {
    const computeQuoteValue = (value: AnalyticsDataPointValueType) => computeCsvQuote(value, separator);
    const result = [];

    const headerRow = generateCsvHeaderRow(computeQuoteValue, selectedMetricId);
    result.push(headerRow.join(separator));

    const rowDates = data.chartData.map((d) => xAccessor(d));
    const yAccessors = data.series.map((serie) => (d: T) => serie.accessor(d));
    const rows = data.chartData.map((d) => yAccessors.map((yAccessor) => computeQuoteValue(yAccessor(d))));

    rows.forEach((row, index) =>
      result.push([computeQuoteValue(rowDates[index])].concat(row).join(separator)),
    );
    return result.join('\n');
  };
}

/**
 * Helper to generate a CSV formatted string for list metrics.
 */
export function generateCsvFormattedDataForList(metricData: AnalyticsTableMetric) {
  return (separator: SeparatorsEnum) => {
    // no data, return empty string on attempted export
    if (isUndefined(metricData)) {
      return '';
    }

    const computeQuoteValue = (value: AnalyticsDataPointValueType) => computeCsvQuote(value, separator);

    const result = [];
    const headers = metricData.columns.map((column) => computeQuoteValue(column.label));
    result.push(headers.join(separator));

    metricData.rows.forEach((row) => {
      result.push(row.map((item) => computeQuoteValue(item.value)).join(separator));
    });

    return result.join('\n');
  };
}

/**
 * Helper to generate a CSV formatted string for heatmap metrics.
 */
export function generateCsvFormattedDataForHeatMap(metricData: AnalyticsHeatMapMetric) {
  return (separator: SeparatorsEnum) => {
    // no data, return empty string on attempted export
    if (isUndefined(metricData) || metricData.values.length === 0) {
      return '';
    }

    const metricsAvailableInDataset = Object.keys(metricData.values[0].values).filter(isScalarMetric);

    const result: Array<string> = [];
    const headers = ['Day', 'Hour']
      .concat(metricsAvailableInDataset.map((m) => getLabelForSubMetric(m)))
      .join(separator);
    result.push(headers);

    metricData.values.forEach((row) => {
      const values = metricsAvailableInDataset.map((metric) => row.values[metric]?.value);
      const rowValues = [renderWeekDayFull(row.day), row.hour].concat(values);
      result.push(rowValues.join(separator));
    });

    return result.join('\n');
  };
}

function getLabelForSubMetric(selectedMetricId: AnalyticsScalarMetricsEnum): string {
  const labels = getMetricLabels(selectedMetricId);

  return labels.label || selectedMetricId;
}

function generateCsvHeaderRow(
  computeQuoteValue: (value: AnalyticsDataPointValueType) => AnalyticsDataPointValueType,
  selectedMetricId?: AnyGraphMetricsEnum,
): Array<AnalyticsDataPointValueType> {
  const labels = getMetricLabels(selectedMetricId);
  const {series} = labels;

  // First column is always the date
  const labelsBase: Array<AnalyticsDataPointValueType> = ['Date'];

  if (isUndefined(series)) {
    return labelsBase.concat([computeQuoteValue(labels.label) || '']);
  }

  return labelsBase.concat(series.map((label) => computeQuoteValue(label.label)));
}
